import type { ReactNode } from 'react';

/**
 * One icon per More-hub destination, keyed by the destination id declared in
 * `src/app/navigation.ts`. Kept separate from the registry itself, which owns
 * routing and entitlement — not presentation.
 *
 * A row with no icon at all is indistinguishable from its neighbour at a
 * glance; this is what turns 18 rows of plain text into a scannable list.
 */

const stroke = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true as const,
};

const MORE_ICONS: Record<string, ReactNode> = {
  transactions: (
    <svg {...stroke}>
      <path d="M4 7h13l-3-3M20 17H7l3 3" />
    </svg>
  ),
  beneficiaries: (
    <svg {...stroke}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      <path d="M16 8.5a2.5 2.5 0 1 0 0-5" />
      <path d="M18 14c2.2.5 3.5 2.2 3.5 5" />
    </svg>
  ),
  fx: (
    <svg {...stroke}>
      <path d="M7 7h11l-3-3M7 7l3 3" />
      <path d="M17 17H6l3-3M17 17l-3 3" />
    </svg>
  ),
  reports: (
    <svg {...stroke}>
      <path d="M5 20V10M12 20V4M19 20v-7" />
    </svg>
  ),
  insights: (
    <svg {...stroke}>
      <path d="M9 18h6M10 21h4" />
      <path d="M12 3a6 6 0 0 0-4 10.5c.6.5 1 1.2 1 2v.5h6v-.5c0-.8.4-1.5 1-2A6 6 0 0 0 12 3Z" />
    </svg>
  ),
  tax: (
    <svg {...stroke}>
      <rect x="4" y="3.5" width="16" height="17" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </svg>
  ),
  accounting: (
    <svg {...stroke}>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <path d="M3.5 9.5h17" />
      <path d="M8 4.5v-1M16 4.5v-1" />
    </svg>
  ),
  expenses: (
    <svg {...stroke}>
      <path d="M6 3.5h9l3 3V20a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1Z" />
      <path d="M9 11h6M9 15h4" />
    </svg>
  ),
  invoices: (
    <svg {...stroke}>
      <path d="M7 3.5h8l3 3V20a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1Z" />
      <path d="M9.5 12.5 11 14l3.5-3.5" />
    </svg>
  ),
  'company-profile': (
    <svg {...stroke}>
      <path d="M4 20V6.5A2.5 2.5 0 0 1 6.5 4h6A2.5 2.5 0 0 1 15 6.5V20" />
      <path d="M15 11h3a2 2 0 0 1 2 2v7" />
      <path d="M8 9h3M8 13h3M8 17h3" />
    </svg>
  ),
  team: (
    <svg {...stroke}>
      <circle cx="9" cy="7.5" r="3" />
      <path d="M3.5 19c0-3 2.5-5.5 5.5-5.5s5.5 2.5 5.5 5.5" />
      <circle cx="17" cy="9" r="2.2" />
      <path d="M15.5 13.2c2 .4 3.5 2 3.5 4.3" />
    </svg>
  ),
  documents: (
    <svg {...stroke}>
      <path d="M7 3.5h7l3 3V20a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1Z" />
      <path d="M9 12h6M9 16h6" />
    </svg>
  ),
  'wallets-spaces': (
    <svg {...stroke}>
      <rect x="3" y="6.5" width="18" height="12" rx="2.2" />
      <path d="M3 10h18" />
      <circle cx="17.5" cy="14" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  ),
  notifications: (
    <svg {...stroke}>
      <path d="M18 9a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7" />
      <path d="M10.5 20a2 2 0 0 0 3 0" />
    </svg>
  ),
  activity: (
    <svg {...stroke}>
      <path d="M3.5 12h4l2.2 5.5L13 6.5l2.2 5.5H20" />
    </svg>
  ),
  profile: (
    <svg {...stroke}>
      <circle cx="12" cy="8.2" r="3.6" />
      <path d="M4.5 20c0-3.9 3.4-7 7.5-7s7.5 3.1 7.5 7" />
    </svg>
  ),
  security: (
    <svg {...stroke}>
      <path d="M12 3.5 5 6.2v5.4c0 4.6 3 7.6 7 9.1 4-1.5 7-4.5 7-9.1V6.2L12 3.5Z" />
      <path d="M9.5 12.2 11.3 14l3.2-3.6" />
    </svg>
  ),
  'continue-on-web': (
    <svg {...stroke}>
      <rect x="3" y="4.5" width="18" height="13" rx="2" />
      <path d="M8 20.5h8M12 17.5v3" />
    </svg>
  ),
};

export function moreIconFor(id: string): ReactNode {
  return MORE_ICONS[id] ?? null;
}
